import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Award, Factory, Globe, Shield, Users, Wrench, TrendingUp, Target, Eye, Heart, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const AboutUs = () => {
  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title="About Alden Technologies | Mineral Water Plant Manufacturer, Jaipur"
        description="Alden Technologies is a leading manufacturer of mineral water bottling plants and beverage machinery in India, with 15+ years of experience and 200+ successful installations."
        path="/about-us"
      />
      <Breadcrumbs items={[{ label: 'About Us' }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green-dark via-alden-green to-alden-green-light text-white py-20 md:py-28">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            About <span className="text-yellow-300">Alden Technologies</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 leading-relaxed">
            India's trusted manufacturer of complete turnkey mineral water bottling plants with 100+ successful installations across 15+ states.
          </p>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Leading Manufacturer of Mineral Water Bottling Plants
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Alden Technologies is a premier manufacturer and supplier of complete turnkey mineral water bottling plants, RO systems, filling machines, and packaging equipment in India.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                With over a decade of experience and 100+ successful installations, we have established ourselves as a trusted partner for businesses looking to enter or expand in the packaged drinking water industry.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Our state-of-the-art manufacturing facility in Jaipur, Rajasthan, equipped with advanced CNC machines, laser cutting technology, and skilled engineering team, enables us to deliver world-class equipment that meets international quality standards.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-alden-green/10 px-6 py-4 rounded-lg">
                  <div className="text-3xl font-bold text-alden-green">100+</div>
                  <div className="text-gray-600 font-semibold">Projects Completed</div>
                </div>
                <div className="bg-alden-green/10 px-6 py-4 rounded-lg">
                  <div className="text-3xl font-bold text-alden-green">15+</div>
                  <div className="text-gray-600 font-semibold">States Covered</div>
                </div>
                <div className="bg-alden-green/10 px-6 py-4 rounded-lg">
                  <div className="text-3xl font-bold text-alden-green">95%</div>
                  <div className="text-gray-600 font-semibold">Client Retention</div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/932fe87d-11cd-44ee-9b8b-ca40c329d944/images/52870b21522d31e2f71037c6540604e73ca73f4f5ad0415bdddaa4945a301960.jpeg" 
                alt="Alden Technologies Manufacturing"
                className="rounded-xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-alden-green/30 hover:shadow-xl transition-all">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-alden-green/10 rounded-full flex items-center justify-center mb-6">
                  <Eye className="w-10 h-10 text-alden-green" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To be India's most trusted and preferred manufacturer of mineral water bottling plants, known for quality, innovation, and customer satisfaction. We envision empowering entrepreneurs across India with world-class equipment and support to succeed in the beverage industry.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-alden-green/30 hover:shadow-xl transition-all">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-alden-green/10 rounded-full flex items-center justify-center mb-6">
                  <Target className="w-10 h-10 text-alden-green" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To deliver complete turnkey mineral water plant solutions with premium quality equipment, expert engineering, timely project execution, and lifetime support. We are committed to helping our clients achieve business success through reliable technology and exceptional service.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Alden */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-alden-green">Alden Technologies</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted by 100+ businesses for quality, reliability, and unmatched after-sales support.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { id: 'about-quality', icon: Award, title: 'Premium Quality', desc: 'Food-grade SS 304/316 machinery with BIS certification' },
              { id: 'about-manufacturing', icon: Factory, title: 'In-house Manufacturing', desc: '15,000 sq.ft facility with advanced CNC & laser technology' },
              { id: 'about-engineering', icon: Wrench, title: 'Expert Engineering', desc: '50+ skilled engineers with decades of industry experience' },
              { id: 'about-warranty', icon: Shield, title: '12 Months Warranty', desc: 'Comprehensive warranty with spare parts and technical support' },
              { id: 'about-service', icon: Globe, title: 'Pan India Service', desc: 'Installation and service support across 15+ states' },
              { id: 'about-retention', icon: TrendingUp, title: '95% Retention Rate', desc: 'Repeat orders from satisfied customers nationwide' }
            ].map((item) => (
              <Card key={item.id} className="border-2 border-alden-green/20 hover:border-alden-green transition-all text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-alden-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-10 h-10 text-alden-green" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Heart className="w-20 h-20 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Quality Commitment</h2>
            <p className="text-xl mb-8 text-white/90 leading-relaxed">
              Quality is at the heart of everything we do. Every machine manufactured at Alden Technologies undergoes rigorous multi-stage quality inspection to ensure zero-defect production. We use only premium-grade SS 304/316 food-grade stainless steel and internationally certified components.
            </p>
            <div className="grid md:grid-cols-4 gap-6">
              {['ISO 9001:2015', 'BIS Certified', 'CE Compliant', 'Food Grade SS'].map((cert) => (
                <div key={`cert-${cert.replace(/[^a-zA-Z0-9]/g, '-')}`} className="bg-white/10 backdrop-blur-sm px-6 py-4 rounded-lg">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                  <div className="font-bold">{cert}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get free consultation and detailed quotation from our expert team. Join 100+ successful businesses.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-alden-green hover:bg-alden-green-light text-white py-6 px-10 text-lg font-semibold">
                <Mail className="mr-2" />
                Request Quotation
              </Button>
            </Link>
            <a href="tel:+919351292512">
              <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-10 text-lg font-semibold">
                <Phone className="mr-2" />
                Call: +91 9351292512
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;